import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeletePoiMutation } from '@/entities/poi'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'

interface PoiDeleteButtonProps {
  poiId: string
  onDeleted?: () => void
}

export function PoiDeleteButton({ poiId, onDeleted }: PoiDeleteButtonProps) {
  const { t } = useTranslation()
  const mutation = useDeletePoiMutation()
  const [showConfirm, setShowConfirm] = useState(false)

  async function handleDelete() {
    try {
      await mutation.mutateAsync(poiId)
      showToast.success(t('poi.deleteSuccess'))
      onDeleted?.()
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        disabled={mutation.isPending}
        onClick={(e) => {
          e.stopPropagation()
          setShowConfirm(true)
        }}
      >
        <Trash2 size={14} className="mr-1.5" />
        {t('common.delete')}
      </Button>
      <ConfirmDialog
        open={showConfirm}
        title={t('poi.deleteConfirm')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={async () => {
          setShowConfirm(false)
          await handleDelete()
        }}
        onCancel={() => setShowConfirm(false)}
        variant="destructive"
      />
    </>
  )
}
